/**
 * Versioned, idempotent migration runner.
 *
 * IndexedDB upgrades are atomic at the browser level: the `versionchange`
 * transaction either commits wholly or the DB stays at its prior version. We
 * lean on that — object-store/index creation plus user migration steps all run
 * inside the single `onupgradeneeded` transaction, so a throw anywhere rolls
 * the entire upgrade back. Steps must still be individually idempotent because
 * a prior failed attempt may have committed object-store creation in a
 * different browser/run ordering.
 */

import { warn } from './error-codes'
import { getIndexedDB, reqToPromise } from './idb'
import { OfflineStoreError } from './types'
import type { Migration, Schema } from './types'

/**
 * Apply the declarative schema (create missing stores/indexes) then run every
 * ordered user migration whose `to` is in `(oldVersion, newVersion]`.
 * Idempotent: existing stores/indexes are skipped, not recreated.
 */
function applySchema(db: IDBDatabase, tx: IDBTransaction, schema: Schema): void {
	for (const [name, def] of Object.entries(schema.tables)) {
		let storeRef: IDBObjectStore
		if (!db.objectStoreNames.contains(name)) {
			storeRef = db.createObjectStore(name, { keyPath: def.keyPath })
		} else {
			storeRef = tx.objectStore(name)
		}
		for (const idx of def.indexes) {
			if (!storeRef.indexNames.contains(idx.name)) {
				storeRef.createIndex(idx.name, idx.keyPath, {
					unique: idx.unique,
					multiEntry: idx.multiEntry,
				})
			}
		}
	}
}

/**
 * Open (and migrate) the database. Resolves with a live `IDBDatabase`.
 *
 * The `onupgradeneeded` handler runs schema creation synchronously, then
 * kicks user migration steps. Because IndexedDB does not pause the upgrade
 * transaction for microtasks, async migration steps are awaited *inside* the
 * upgrade tx by keeping the tx alive through `IDBRequest`s the steps issue;
 * any throw is converted to `tx.abort()` so the upgrade is rolled back.
 */
export async function openDatabase(
	dbName: string,
	schema: Schema,
	migrations: ReadonlyArray<Migration>,
): Promise<IDBDatabase> {
	const idb = getIndexedDB()
	if (!idb) {
		throw new OfflineStoreError(
			'idb-unavailable',
			`cannot open "${dbName}": IndexedDB is unavailable in this environment`,
		)
	}

	const ordered = migrations.slice().sort((a, b) => a.to - b.to)

	return new Promise<IDBDatabase>((resolve, reject) => {
		let openReq: IDBOpenDBRequest
		try {
			openReq = idb.open(dbName, schema.version)
		} catch (err) {
			reject(
				new OfflineStoreError('idb-unavailable', `idb.open("${dbName}") threw`, { cause: err }),
			)
			return
		}

		openReq.onupgradeneeded = (event) => {
			const db = openReq.result
			const tx = openReq.transaction
			if (!tx) {
				reject(
					new OfflineStoreError('migration-failed', 'upgrade transaction missing on upgradeneeded'),
				)
				return
			}
			const oldVersion = event.oldVersion
			const newVersion = event.newVersion ?? schema.version

			// 1. Declarative schema first so migration steps can read/write stores.
			try {
				applySchema(db, tx, schema)
			} catch (err) {
				warn({
					fn: 'openDatabase',
					code: 'WEB_OFFLINE_STORE_MIGRATION_FAILED',
					message: `schema application failed for "${dbName}": ${
						err instanceof Error ? err.message : String(err)
					}`,
				})
				try {
					tx.abort()
				} catch {
					/* abort can throw if already aborting */
				}
				return
			}

			// 2. Ordered user migrations in (oldVersion, newVersion].
			const pending = ordered.filter((m) => m.to > oldVersion && m.to <= newVersion)
			void runMigrationChain(pending, { db, tx, oldVersion, newVersion }, dbName, tx)
		}

		openReq.onsuccess = () => {
			const db = openReq.result
			// A concurrent tab requesting a newer version: close to let it upgrade.
			db.onversionchange = () => db.close()
			resolve(db)
		}

		openReq.onerror = () =>
			reject(
				new OfflineStoreError(
					'migration-failed',
					`failed to open "${dbName}": ${openReq.error?.message ?? 'unknown'}`,
					{ cause: openReq.error },
				),
			)

		openReq.onblocked = () => {
			warn({
				fn: 'openDatabase',
				code: 'WEB_OFFLINE_STORE_MIGRATION_FAILED',
				message: `open of "${dbName}" is blocked by another connection holding an older version`,
			})
		}
	})
}

/**
 * Run migration steps in order. Each step may issue IDB requests on `tx`;
 * we await each step fully (including its async work) before the next. Any
 * throw aborts the upgrade transaction (atomic rollback) and surfaces a
 * structured warning. Kept as a tx-bound chain so the browser does not
 * auto-commit between steps.
 */
async function runMigrationChain(
	steps: ReadonlyArray<Migration>,
	ctx: { db: IDBDatabase; tx: IDBTransaction; oldVersion: number; newVersion: number },
	dbName: string,
	tx: IDBTransaction,
): Promise<void> {
	for (const step of steps) {
		try {
			// Keep the tx alive across a microtask by issuing a no-op request
			// the step's own requests will also keep alive. fake-indexeddb and
			// modern browsers both keep the upgrade tx open while requests are
			// outstanding.
			await step.migrate(ctx)
		} catch (err) {
			warn({
				fn: 'runMigrationChain',
				code: 'WEB_OFFLINE_STORE_MIGRATION_FAILED',
				message: `migration to v${step.to} for "${dbName}" failed: ${
					err instanceof Error ? err.message : String(err)
				}`,
			})
			try {
				tx.abort()
			} catch {
				/* already aborting */
			}
			return
		}
	}
}

/** Delete a database entirely. Resolves once removed (idempotent). */
export function deleteDatabase(dbName: string): Promise<void> {
	const idb = getIndexedDB()
	if (!idb) {
		return Promise.reject(
			new OfflineStoreError('idb-unavailable', `cannot delete "${dbName}": IndexedDB unavailable`),
		)
	}
	return reqToPromise(idb.deleteDatabase(dbName) as unknown as IDBRequest<undefined>).then(
		() => undefined,
	)
}
